import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BarList, DistributionBar } from '../../../components/design-system/analytics';
import { KpiCard, PillTabs, SectionCard, formatDate, formatNumber } from '../../../components/design-system/admin';
import { PageContainer } from '../../../common/components/PageContainer';
import { RevenueReport, useCommerceApi } from '../../../common/hooks/use-commerce-api.hook';
import { ROUTES } from '../../../modules/vendor/market/insight-spec';
import { SALES_RANGES, SalesRange, periodsFor, returningShare, revenueInsights, channelName } from '../../../modules/vendor/market/sales-insights';
import { Reading } from '../market/market-shared';

const money = (value: number, currency: string | null, locale: string) => {
    try {
        return new Intl.NumberFormat(locale, {
            style: currency ? 'currency' : 'decimal',
            currency: currency ?? undefined,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        // An unknown currency code must not take the page down with it.
        return `${Math.round(value)} ${currency ?? ''}`.trim();
    }
};

/**
 * What actually sold.
 *
 * The reason the shop connection exists: every other number in this product is
 * a proxy for sales, and this one is sales. It opens with the reading against
 * the period before — up or down, and why — then the four numbers with their
 * change, where it sold, how much of the shop's catalogue Ardealy can show,
 * and what sold, with the products Ardealy cannot help with marked.
 */
export const RevenuePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const api = useCommerceApi();

    const [range, setRange] = useState<SalesRange>('90d');
    const [data, setData] = useState<RevenueReport | null>(null);
    const [previous, setPrevious] = useState<RevenueReport | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const p = periodsFor(range);
        setLoading(true);
        // The period before is a second, cheap read of the same report; without
        // it every number here is a level with nothing to say whether it is good.
        Promise.all([api.revenue(p.current), api.revenue(p.previous).catch(() => null)])
            .then(([cur, prev]) => {
                if (!alive) return;
                setData(cur);
                setPrevious(prev);
            })
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [api, range]);

    const currency = data?.currency ?? null;
    const fmt = (v: number) => money(v, currency, i18n.language);
    const specs = useMemo(() => (data?.connected ? revenueInsights(data, previous) : []), [data, previous]);
    const prevHint = t('BUSINESS.SALES.VS_PREVIOUS');
    const ret = returningShare(data);
    const prevRet = returningShare(previous);

    return (
        <PageContainer breadcrumbs={[{ label: t('SALES.TITLE') }, { label: t('SALES.REVENUE.TITLE') }]}>
            <div className="mx-auto flex w-full min-w-0 max-w-[1100px] flex-col gap-6" data-testid="sales-revenue">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('SALES.REVENUE.TITLE')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{t('SALES.REVENUE.SUBTITLE')}</p>
                    </div>
                    {data?.connected && (
                        <PillTabs
                            value={range}
                            onChange={setRange}
                            ariaLabel={t('BUSINESS.SALES.RANGE')}
                            options={SALES_RANGES.map((r) => ({ value: r, label: t(`BUSINESS.SALES.RANGE_${r.toUpperCase()}`) }))}
                        />
                    )}
                </div>

                {loading && !data && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {!loading && !data?.connected && (
                    <div className="flex flex-col items-start gap-4 rounded-xl border bg-card p-8">
                        <div>
                            <p className="text-[15px] font-medium">{t('SALES.REVENUE.NOT_CONNECTED')}</p>
                            <p className="mt-1.5 max-w-[560px] text-sm leading-relaxed text-muted-foreground">{t('SALES.REVENUE.NOT_CONNECTED_BODY')}</p>
                        </div>
                        <Button variant="outline" size="lg" onClick={() => navigate(ROUTES.shop)}>
                            {t('SALES.REVENUE.CONNECT')}
                            <ArrowUpRight data-icon="inline-end" className="rtl:-scale-x-100" />
                        </Button>
                    </div>
                )}

                {data?.connected && (
                    <>
                        <p className="-mt-2 text-xs text-muted-foreground">
                            {t('SALES.REVENUE.PERIOD', { from: formatDate(data.period.from), to: formatDate(data.period.to) })}
                            {loading && <Loader2 className="ms-2 inline size-3 animate-spin" />}
                        </p>

                        <Reading specs={specs} />

                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <KpiCard
                                label={t('SALES.REVENUE.TOTAL')}
                                value={fmt(data.revenue)}
                                delta={previous ? { current: data.revenue, previous: previous.revenue } : undefined}
                                hint={previous ? t('BUSINESS.SALES.WAS', { value: fmt(previous.revenue) }) : prevHint}
                            />
                            <KpiCard
                                label={t('SALES.REVENUE.ORDERS')}
                                value={formatNumber(data.orders)}
                                delta={previous ? { current: data.orders, previous: previous.orders } : undefined}
                                hint={previous ? t('BUSINESS.SALES.WAS', { value: formatNumber(previous.orders) }) : prevHint}
                            />
                            <KpiCard
                                label={t('SALES.REVENUE.AVERAGE')}
                                value={fmt(data.averageOrder)}
                                delta={previous ? { current: data.averageOrder, previous: previous.averageOrder } : undefined}
                                hint={previous ? t('BUSINESS.SALES.WAS', { value: fmt(previous.averageOrder) }) : prevHint}
                            />
                            <KpiCard
                                label={t('SALES.REVENUE.RETURNING')}
                                value={ret != null ? `${formatNumber(Math.round(ret * 100))}%` : '—'}
                                hint={prevRet != null ? t('BUSINESS.SALES.WAS', { value: `${formatNumber(Math.round(prevRet * 100))}%` }) : t('SALES.REVENUE.RETURNING_HINT')}
                            />
                        </div>

                        <div className="grid gap-4 lg:grid-cols-2">
                            {data.byChannel.length > 0 && (
                                <SectionCard title={t('SALES.REVENUE.CHANNELS')} description={t('SALES.REVENUE.CHANNELS_HINT')}>
                                    <DistributionBar
                                        columns={2}
                                        format={fmt}
                                        segments={data.byChannel.map((row) => ({
                                            key: row.channel,
                                            label: t(`SALES.CHANNEL.${String(row.channel).toUpperCase()}`, { defaultValue: channelName(row.channel) }),
                                            value: row.revenue,
                                        }))}
                                    />
                                </SectionCard>
                            )}
                            {data.catalogue.shopProducts > 0 && (
                                <SectionCard
                                    title={t('BUSINESS.SALES.CATALOGUE')}
                                    description={t('BUSINESS.SALES.CATALOGUE_HINT')}
                                    action={
                                        data.catalogue.unlinked > 0 ? (
                                            <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.catalog)}>
                                                {t('BUSINESS.ACTION.ADD_PRODUCTS')}
                                            </Button>
                                        ) : undefined
                                    }
                                >
                                    <DistributionBar
                                        columns={2}
                                        segments={[
                                            { key: 'linked', label: t('BUSINESS.SALES.LINKED'), value: data.catalogue.linked, tone: 'good' },
                                            { key: 'unlinked', label: t('SALES.REVENUE.NOT_IN_ARDEALY'), value: data.catalogue.unlinked, tone: data.catalogue.unlinked ? 'bad' : undefined },
                                        ]}
                                    />
                                </SectionCard>
                            )}
                        </div>

                        <SectionCard title={t('BUSINESS.SALES.TOP_PRODUCTS')} description={t('BUSINESS.SALES.TOP_PRODUCTS_HINT')} testId="sales-products">
                            <BarList
                                format={fmt}
                                emptyText={t('SALES.REVENUE.NO_ORDERS')}
                                items={data.byProduct.map((row, i) => ({
                                    key: `${row.sku ?? row.title ?? i}`,
                                    label: (
                                        <span className="flex min-w-0 items-center gap-2">
                                            <span className="truncate">{row.title || row.sku || '—'}</span>
                                            {!row.inArdealy && (
                                                <Badge variant="outline" className="shrink-0 text-destructive">
                                                    {t('SALES.REVENUE.NOT_IN_ARDEALY')}
                                                </Badge>
                                            )}
                                        </span>
                                    ),
                                    value: row.revenue,
                                    secondary: t('BUSINESS.SALES.UNITS', { count: row.units, n: formatNumber(row.units) }),
                                }))}
                            />
                        </SectionCard>
                    </>
                )}
            </div>
        </PageContainer>
    );
};

export default RevenuePage;
